import { supabase } from '@/integrations/supabase/client';
import { Course } from '@/hooks/useCourses';
import { Module } from '@/types/courseTypes';

export interface DatabaseModuleContent {
  id: string;
  module_id: string;
  course_id: string;
  content: string;
  objectives: string[];
  prerequisites: string[];
  key_concepts: string[];
  further_reading: any;
  video_url?: string;
  estimated_time: number;
  difficulty_level: string;
  learning_outcomes: string[];
}

export interface DatabaseCodeExample {
  id: string;
  module_id: string;
  course_id: string;
  title: string;
  language: string;
  code: string;
  explanation?: string;
  example_order: number;
  complexity_level: string;
  runnable: boolean;
}

export interface DatabaseExercise {
  id: string;
  module_id: string;
  course_id: string;
  title: string;
  description?: string;
  starter_code?: string;
  solution?: string;
  hints: string[];
  exercise_order: number;
  difficulty: string;
  estimated_time: number;
  exercise_type: string;
  validation_rules: any;
}

export interface DatabaseQuiz {
  id: string;
  module_id: string;
  course_id: string;
  quiz_title: string;
  questions: any; // JSON from database
  pass_threshold: number;
  time_limit?: number;
  max_attempts: number;
  quiz_type: string;
  randomize_questions: boolean;
  show_feedback: boolean;
}

export interface DatabasePractice {
  id: string;
  module_id: string;
  course_id: string;
  practice_type: string;
  title: string;
  description?: string;
  instructions: any;
  starter_files: any;
  solution_files: any;
  evaluation_criteria: string[];
  estimated_time: number;
  required_tools: string[];
}

/**
 * Universal Database Course Service
 * Replaces all static course imports with database queries
 */
export class DatabaseCourseService {
  /**
   * Get all courses from database (replaces getAllCourses from static files)
   */
  static async getAllCourses(): Promise<Course[]> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('is_active', true)
      .order('title');

    if (error) {
      console.error('Error fetching all courses:', error);
      throw error;
    }

    return (data || []).map(course => ({
      ...course,
      course_status: (course.course_status || 'active') as 'active' | 'coming_soon' | 'archived' | 'draft'
    }));
  }

  /**
   * Get courses by category (replaces SQL_COURSES, PYTHON_COURSES, etc.)
   */
  static async getCoursesByCategory(category: string): Promise<Course[]> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('category', category)
      .eq('is_active', true)
      .order('title');

    if (error) {
      console.error(`Error fetching courses for category ${category}:`, error);
      throw error;
    }

    return (data || []).map(course => ({
      ...course,
      course_status: (course.course_status || 'active') as 'active' | 'coming_soon' | 'archived' | 'draft'
    }));
  }

  /**
   * Get specific course by ID
   */
  static async getCourseById(courseId: string): Promise<Course | null> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('id', courseId)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      console.error(`Error fetching course ${courseId}:`, error);
      throw error;
    }

    return data ? {
      ...data,
      course_status: (data.course_status || 'active') as 'active' | 'coming_soon' | 'archived' | 'draft'
    } : null;
  }

  /**
   * Get modules for a course (replaces static module imports)
   * Returns all modules (backward compatible)
   */
  static async getCourseModules(courseId: string): Promise<Module[]> {
    const result = await this.getCourseModulesPaginated(courseId, { page: 1, pageSize: 1000 });
    
    // SAFETY FILTER: Explicitly filter out disabled modules by ID
    const DISABLED_MODULE_IDS = ['openai-api-mastery'];
    return result.modules.filter(m => !DISABLED_MODULE_IDS.includes(m.id));
  }

  /**
   * PHASE 2: Get modules with pagination support
   */
  static async getCourseModulesPaginated(
    courseId: string,
    options?: { page?: number; pageSize?: number }
  ): Promise<{ modules: Module[]; total: number; hasMore: boolean }> {
    const page = options?.page || 1;
    const pageSize = options?.pageSize || 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error, count } = await supabase
      .from('course_modules')
      .select('*', { count: 'exact' })
      .eq('course_id', courseId)
      .eq('is_enabled', true)
      .order('module_order')
      .range(from, to);

    if (error) {
      console.error(`Error fetching modules for course ${courseId}:`, error);
      throw error;
    }

    // Transform to Module format
    const modules = (data || []).map(moduleData => ({
      id: moduleData.module_id,
      title: moduleData.module_title,
      description: `Module ${moduleData.module_order}`,
      content: '', // Will be loaded separately
      order: moduleData.module_order,
      estimatedTime: `${moduleData.estimated_duration || 30} minutes`,
      duration: `${moduleData.estimated_duration || 30} minutes`,
      difficulty: moduleData.difficulty_level as 'beginner' | 'intermediate' | 'advanced',
      objectives: [],
      codeExamples: [],
      exercises: [],
      quickCheck: { questions: [] }
    }));

    // SAFETY FILTER: Explicitly filter out disabled modules
    const DISABLED_MODULE_IDS = ['openai-api-mastery'];
    const filteredModules = modules.filter(m => !DISABLED_MODULE_IDS.includes(m.id));

    return {
      modules: filteredModules,
      total: filteredModules.length,
      hasMore: (count || 0) > to + 1
    };
  }

  /**
   * Get detailed module content (replaces static content files)
   */
  static async getModuleContent(moduleId: string, courseId: string): Promise<DatabaseModuleContent | null> {
    const { data, error } = await supabase
      .from('module_content')
      .select('*')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      console.error(`Error fetching content for module ${moduleId}:`, error);
      throw error;
    }

    return data;
  }

  /**
   * Get code examples for a module (replaces static example files)
   */
  static async getModuleCodeExamples(moduleId: string, courseId: string): Promise<DatabaseCodeExample[]> {
    const { data, error } = await supabase
      .from('module_code_examples')
      .select('*')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .order('example_order');

    if (error) {
      console.error(`Error fetching code examples for module ${moduleId}:`, error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get exercises for a module (replaces static exercise files)
   */
  static async getModuleExercises(moduleId: string, courseId: string): Promise<DatabaseExercise[]> {
    const { data, error } = await supabase
      .from('module_exercises')
      .select('*')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .order('exercise_order');

    if (error) {
      console.error(`Error fetching exercises for module ${moduleId}:`, error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get quiz for a module (replaces static quiz files)
   */
  static async getModuleQuiz(moduleId: string, courseId: string): Promise<DatabaseQuiz | null> {
    const { data, error } = await supabase
      .from('module_quizzes')
      .select('*')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      console.error(`Error fetching quiz for module ${moduleId}:`, error);
      throw error;
    }

    return data;
  }

  /**
   * Get practice activities for a module
   */
  static async getModulePractice(moduleId: string, courseId: string): Promise<DatabasePractice[]> {
    const { data, error } = await supabase
      .from('module_practice')
      .select('*')
      .eq('module_id', moduleId)
      .eq('course_id', courseId);

    if (error) {
      console.error(`Error fetching practice for module ${moduleId}:`, error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get complete module data with all components
   * PHASE 1: Optimized with parallel queries instead of waterfall
   */
  static async getCompleteModule(moduleId: string, courseId: string): Promise<Module | null> {
    try {
      // PHASE 1 OPTIMIZATION: Batch all queries in parallel (was sequential before)
      const [moduleData, content, examples, exercises, quiz, practice] = await Promise.all([
        supabase
          .from('course_modules')
          .select('*')
          .eq('module_id', moduleId)
          .eq('course_id', courseId)
          .eq('is_enabled', true)
          .single(),
        this.getModuleContent(moduleId, courseId),
        this.getModuleCodeExamples(moduleId, courseId),
        this.getModuleExercises(moduleId, courseId),
        this.getModuleQuiz(moduleId, courseId),
        this.getModulePractice(moduleId, courseId)
      ]);

      if (moduleData.error) {
        if (moduleData.error.code === 'PGRST116') return null;
        throw moduleData.error;
      }

      const module = moduleData.data;

      const moduleConfig = typeof module.module_config === 'object' ? module.module_config as Record<string, any> : {};
      
      return {
        id: module.module_id,
        title: module.module_title,
        description: moduleConfig?.description || content?.learning_outcomes?.[0] || `Module ${module.module_order}`,
        content: content?.content || '',
        order: module.module_order,
        estimatedTime: `${module.estimated_duration || 30} minutes`,
        duration: `${module.estimated_duration || 30} minutes`,
        difficulty: module.difficulty_level as 'beginner' | 'intermediate' | 'advanced',
        objectives: content?.objectives || [],
        codeExamples: examples.map(ex => ({
          title: ex.title,
          code: ex.code,
          explanation: ex.explanation,
          language: ex.language
        })),
        exercises: exercises.map((ex, index) => ({
          id: ex.id,
          title: ex.title,
          description: ex.description || '',
          starterCode: ex.starter_code || '',
          solution: ex.solution || '',
          hints: ex.hints
        })),
        practice: practice.length > 0 ? {
          task: practice[0].title,
          starter: JSON.stringify(practice[0].starter_files),
          solution: JSON.stringify(practice[0].solution_files),
          hints: practice[0].evaluation_criteria
        } : undefined,
        quickCheck: quiz ? {
          questions: Array.isArray(quiz.questions) ? quiz.questions.map((q: any, index: number) => ({
            id: `${moduleId}-q${index + 1}`,
            question: q.question,
            options: q.options,
            correctAnswer: q.correctAnswer,
            correctAnswerIndex: q.correctAnswer,
            difficulty: q.difficulty || 'medium',
            explanation: q.explanation
          })) : []
        } : { questions: [] },
        furtherReading: content?.further_reading ? (
          Array.isArray(content.further_reading) ? content.further_reading.map((resource: any) => ({
            title: resource.title,
            url: resource.url,
            type: resource.type || 'reference',
            description: resource.description,
            isPrimary: resource.isPrimary || false
          })) : []
        ) : undefined
      };
    } catch (error) {
      console.error(`Error getting complete module ${moduleId}:`, error);
      throw error;
    }
  }

  /**
   * Get course categories with course counts
   */
  static async getCategoriesWithCounts() {
    const { data, error } = await supabase
      .rpc('get_categories_with_course_counts');

    if (error) {
      console.error('Error fetching categories with counts:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Search courses by text query
   */
  static async searchCourses(query: string): Promise<Course[]> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('is_active', true)
      .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
      .order('title');

    if (error) {
      console.error('Error searching courses:', error);
      throw error;
    }

    return (data || []).map(course => ({
      ...course,
      course_status: (course.course_status || 'active') as 'active' | 'coming_soon' | 'archived' | 'draft'
    }));
  }
}